/**
 * Irmão: `src/app/components/filters/ActiveFilters.tsx`.
 *
 * Duplicação deliberada (openspec/changes/flashcards-revisao-redesign/design.md,
 * Decisão 2). Não reusar `components/filters/`: store, persistência e efeito de
 * aplicação são de outra tela. Uma correção visual aqui deve ser conferida
 * no irmão, e o inverso.
 */
import { useState } from "react";
import { X } from "lucide-react";
import { REVISAO_FILTER_FOCUS_RING, revisaoFilterCx } from "./tokens";

export interface RevisaoActiveFilterToken {
  dimension: string;
  dimensionLabel: string;
  /** Valor guardado. Remover usa este, não o texto da ficha. */
  value: string;
  /**
   * Texto depois da dimensão. Quando falta, a ficha mostra `value`.
   * Matéria passa o nome aqui e o id em `value`, para a ficha não exibir o id.
   */
  label?: string;
}

export interface RevisaoActiveFiltersProps {
  items: RevisaoActiveFilterToken[];
  onRemove: (item: RevisaoActiveFilterToken) => void;
  onClearAll: () => void;
  /**
   * `panel` — the card.
   * `inline` — horizontal row for a narrow viewport.
   * `bar` — wraps; past maxVisible it collapses to a link instead of sideways scroll.
   */
  variant?: "panel" | "inline" | "bar";
  maxVisible?: number; // default 3, bar only
  className?: string;
}

/**
 * What is currently affecting the review query.
 *
 * Every token is prefixed with its dimension so it stays readable out of
 * context, removes exactly one value, and truncates at the end with the full
 * text kept in `title`. `Limpar tudo` is neutral, not red — clearing a filter
 * destroys nothing the user cannot immediately redo.
 */
export default function RevisaoActiveFilters({
  items,
  onRemove,
  onClearAll,
  variant = "panel",
  maxVisible = 3,
  className,
}: RevisaoActiveFiltersProps) {
  const isPanel = variant === "panel";
  const [expanded, setExpanded] = useState(false);

  const linkClass = revisaoFilterCx(
    "shrink-0 rounded-chip px-2 py-1 text-xs font-medium",
    "text-brand transition-colors hover:bg-surface-hover",
    REVISAO_FILTER_FOCUS_RING,
  );

  const clearClass = revisaoFilterCx(
    "shrink-0 rounded-chip px-2 py-1 text-xs font-medium",
    "text-ink-secondary transition-colors hover:bg-surface-hover hover:text-ink",
    REVISAO_FILTER_FOCUS_RING,
  );

  const tokens = items.map((item) => {
    const shown = item.label && item.label !== "" ? item.label : item.value;
    const text = `${item.dimensionLabel}: ${shown}`;
    return (
      <span
        key={`${item.dimension}:${item.value}`}
        className={revisaoFilterCx(
          "inline-flex h-[26px] max-w-full shrink-0 items-center gap-1 rounded-full",
          "border border-edge bg-surface-muted pl-2.5 pr-1 text-xs text-ink-secondary",
        )}
      >
        <span className="truncate" title={text}>
          {text}
        </span>
        <button
          type="button"
          onClick={() => onRemove(item)}
          aria-label={`Remover filtro ${text}`}
          title={`Remover filtro ${text}`}
          className={revisaoFilterCx(
            "grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full",
            "text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink",
            REVISAO_FILTER_FOCUS_RING,
          )}
        >
          <X className="h-3 w-3" aria-hidden="true" />
        </button>
      </span>
    );
  });

  if (variant === "bar") {
    if (items.length === 0) return null;

    const collapsed = items.length > maxVisible && !expanded;

    return (
      <div
        className={revisaoFilterCx("flex flex-wrap items-center gap-x-2 gap-y-1.5", className)}
        role="group"
        aria-label="Filtros selecionados"
      >
        {collapsed ? (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            aria-expanded={false}
            className={linkClass}
          >
            {`Ver os ${items.length} filtros selecionados`}
          </button>
        ) : (
          <>
            {tokens}
            {items.length > maxVisible ? (
              <button
                type="button"
                onClick={() => setExpanded(false)}
                aria-expanded
                className={linkClass}
              >
                Ocultar
              </button>
            ) : null}
            <button type="button" onClick={onClearAll} className={clearClass}>
              Limpar tudo
            </button>
          </>
        )}
      </div>
    );
  }

  if (!isPanel) {
    if (items.length === 0) return null;
    return (
      <div
        className={revisaoFilterCx("flex items-center gap-2 overflow-x-auto pb-1", className)}
        role="group"
        aria-label="Filtros selecionados"
      >
        {tokens}
        <button type="button" onClick={onClearAll} className={clearClass}>
          Limpar tudo
        </button>
      </div>
    );
  }

  return (
    <div
      className={revisaoFilterCx("rounded-card border border-edge bg-surface p-4", className)}
      role="group"
      aria-label="Filtros selecionados"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-[13px] font-semibold text-ink">Filtros selecionados</h3>
        <span
          className="rounded-full bg-select/[0.14] px-2 py-0.5 text-[11px] font-semibold tabular-nums text-select-ink"
          aria-label={`${items.length} filtros selecionados`}
        >
          {items.length}
        </span>
      </div>

      {items.length === 0 ? (
        <p className="mt-3 text-xs text-ink-muted">
          Nenhum filtro selecionado. Escolha uma matéria, uma palavra-chave ou um baralho para começar.
        </p>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap gap-1.5">{tokens}</div>
          <button type="button" onClick={onClearAll} className={revisaoFilterCx("mt-3", clearClass)}>
            Limpar tudo
          </button>
        </>
      )}
    </div>
  );
}
